import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import './index.css';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { toast, Toaster } from './components/ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        localStorage.removeItem('token');
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!res.ok) throw new Error('Invalid credentials');
    const data = await res.json();
    localStorage.setItem('token', data.access_token);
    await fetchUser(data.access_token);
    return data;
  };

  const register = async (email, password, role = 'editor') => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, role })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Registration failed');
    }
    const data = await res.json();
    localStorage.setItem('token', data.access_token);
    await fetchUser(data.access_token);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Sidebar Ad Component - Full height ad banner
const SidebarAd = ({ ad, size = 'large' }) => {
  const handleClick = async () => {
    try {
      // Track the click
      await fetch(`${API_URL}/api/ads/${ad.id}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page_url: window.location.href })
      });
    } catch (err) {
      console.error('Failed to track click:', err);
    }
    // Open the ad link
    if (ad.click_url) {
      window.open(ad.click_url, '_blank');
    }
  };

  return (
    <div 
      className="sidebar-ad cursor-pointer group flex-1 min-h-[200px]" 
      onClick={handleClick}
      data-testid={`ad-${ad.position}-${ad.id}`}
    >
      <div className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.01] h-full bg-gradient-to-b from-slate-900 to-slate-800">
        {ad.is_premium && (
          <div className="absolute top-3 left-3 z-10">
            <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs px-2 py-1">
              ⭐ Featured
            </Badge>
          </div>
        )}
        
        {/* Ad Image/Video - Takes most of the space */}
        <div className="h-[60%] overflow-hidden">
          {ad.ad_type === 'video' ? (
            <video 
              src={ad.media_url} 
              className="w-full h-full object-cover"
              autoPlay 
              muted 
              loop 
              playsInline
            />
          ) : (
            <img 
              src={ad.media_url} 
              alt={ad.title}
              className="w-full h-full object-cover"
            />
          )}
        </div>
        
        {/* Ad Content - Bottom section */}
        <div className="h-[40%] p-4 flex flex-col justify-between bg-gradient-to-t from-black/90 via-black/70 to-transparent">
          <div>
            <h4 className="text-white font-bold text-base leading-tight">{ad.business_name}</h4>
            <p className="text-white/70 text-sm mt-2 line-clamp-3">{ad.description}</p>
          </div>
          
          <div className="mt-3">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-sm font-medium px-4 py-2 rounded-full hover:from-cyan-400 hover:to-teal-400 transition-all">
              <span>🔗</span> Learn More
            </div>
          </div>
        </div>
        
        {/* Decorative border */}
        <div className="absolute inset-0 rounded-xl border-2 border-white/10 pointer-events-none"></div>
      </div>
      
      <div className="text-center mt-2">
        <span className="text-[10px] text-muted-foreground/40 uppercase tracking-widest">Sponsored</span>
      </div>
    </div>
  );
};

// Ad Sidebar Container - Full height sidebars from top to bottom
const AdSidebars = () => {
  const [leftAds, setLeftAds] = useState([]);
  const [rightAds, setRightAds] = useState([]);

  useEffect(() => {
    // Fetch ads for each position
    fetch(`${API_URL}/api/ads/public?position=left`)
      .then(res => res.json())
      .then(data => setLeftAds(data.ads || []))
      .catch(console.error);
    
    fetch(`${API_URL}/api/ads/public?position=right`)
      .then(res => res.json())
      .then(data => setRightAds(data.ads || []))
      .catch(console.error);
  }, []);

  // Duplicate ads to fill the sidebar if needed
  const fillSidebar = (ads, minCount = 3) => {
    if (ads.length === 0) return [];
    const result = [...ads];
    while (result.length < minCount) {
      result.push(...ads.map((ad, i) => ({ ...ad, id: `${ad.id}-dup-${result.length + i}` })));
    }
    return result.slice(0, minCount);
  };

  const leftAdsFilled = fillSidebar(leftAds, 3);
  const rightAdsFilled = fillSidebar(rightAds, 3);

  return (
    <>
      {/* Left Sidebar - Full height */}
      {leftAds.length > 0 && (
        <div className="fixed left-2 top-20 bottom-4 z-30 hidden xl:flex flex-col w-[200px] gap-3 overflow-hidden">
          {leftAdsFilled.map((ad, index) => (
            <SidebarAd key={ad.id || index} ad={ad} />
          ))}
        </div>
      )}
      
      {/* Right Sidebar - Full height */}
      {rightAds.length > 0 && (
        <div className="fixed right-2 top-20 bottom-4 z-30 hidden xl:flex flex-col w-[200px] gap-3 overflow-hidden">
          {rightAdsFilled.map((ad, index) => (
            <SidebarAd key={ad.id || index} ad={ad} />
          ))}
        </div>
      )}
    </>
  );
};

// Header Component
const Header = () => {
  const { user, logout } = useAuth();

  return (
    <header data-testid="site-header" className="sticky top-0 z-40 fiji-glass-water border-b fiji-tapa-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <nav className="flex h-16 items-center justify-between">
          <Link to="/" className="font-[var(--font-heading)] text-2xl tracking-tight tropical-text-gradient flex items-center gap-2">
            <span className="text-3xl">🌴</span>
            Cephas Kumar
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/properties" className="text-sm hover:text-[hsl(var(--fiji-lagoon))] transition-colors font-medium">Properties</Link>
            {user ? (
              <>
                <Link to="/admin" className="text-sm hover:text-[hsl(var(--fiji-lagoon))] transition-colors font-medium">Admin</Link>
                <Button onClick={logout} variant="ghost" size="sm">Logout</Button>
              </>
            ) : (
              <Link to="/login"><Button size="sm" className="fiji-button">Sign In</Button></Link>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

// Home Page
const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    // Fetch featured properties
    fetch(`${API_URL}/api/listings?featured=true&limit=6`)
      .then(res => res.json())
      .then(data => setFeatured(data.listings || []))
      .catch(console.error);
    
    // Fetch upcoming (draft) properties
    fetch(`${API_URL}/api/upcoming-properties?limit=6`)
      .then(res => res.json())
      .then(data => setUpcoming(data.upcoming || []))
      .catch(console.error);
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden tropical-ocean-gradient fiji-tapa-pattern palm-frond-top">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-32 palm-leaf-shadow coconut-leaf-pattern">
          <div className="max-w-3xl animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-5xl">🏝️</span>
              <span className="text-lg font-semibold text-[hsl(var(--fiji-ocean))]">Fiji Islands Real Estate</span>
            </div>
            <h1 className="font-[var(--font-heading)] text-5xl sm:text-6xl lg:text-7xl tracking-tight tropical-text-gradient leading-tight mb-6">
              Your Gateway to Paradise Living in Fiji
            </h1>
            <p className="mt-6 text-xl text-[hsl(var(--fiji-ocean))] leading-relaxed">
              Discover exceptional beachfront properties, luxury villas, and tropical estates across Fiji's pristine islands. From the crystal-clear waters of Denarau to the lush landscapes of Taveuni, find your perfect island home.
            </p>
            <div className="mt-8 flex gap-4 items-center">
              <Link to="/properties">
                <Button size="lg" className="fiji-button text-lg px-8 py-6">
                  <span className="mr-2">🌊</span>
                  Explore Fiji Properties
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-sm text-[hsl(var(--fiji-ocean))]">
                <span className="text-2xl">🥥</span>
                <span>🌺</span>
                <span>🏖️</span>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      <section className="py-20 md:py-32 bg-gradient-to-b from-white via-[#F5FBFD] to-white wave-pattern coconut-leaf-pattern">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 tropical-leaf-border gold-palm-leaf-left gold-palm-leaf-right">
            <div className="flex items-center justify-center gap-3 mb-4 gold-palm-header">
              <span className="text-4xl">🌴</span>
              <h2 className="font-[var(--font-heading)] text-4xl md:text-5xl gold-text-gradient">Featured Fiji Properties</h2>
              <span className="text-4xl">🌴</span>
            </div>
            <p className="text-lg text-muted-foreground">Handpicked luxury estates on pristine white sand beaches and crystal-clear lagoons</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map(listing => (
              <PropertyCard key={listing.id} listing={listing} />
            ))}
          </div>
          {featured.length === 0 && (
            <div className="text-center py-12 fiji-luxury-card rounded-xl p-12 fiji-premium-shadow">
              <p className="text-lg text-muted-foreground">No featured properties at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Properties Section */}
      {upcoming.length > 0 && (
        <section className="py-20 md:py-28 bg-gradient-to-b from-amber-50 via-orange-50 to-amber-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-400 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 animate-pulse">
                <span>🔥</span> Coming Soon <span>🔥</span>
              </div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-4xl">✨</span>
                <h2 className="font-[var(--font-heading)] text-4xl md:text-5xl bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 bg-clip-text text-transparent">
                  Upcoming Properties
                </h2>
                <span className="text-4xl">✨</span>
              </div>
              <p className="text-lg text-amber-800 max-w-2xl mx-auto">
                🌟 Exciting new listings on the horizon! Our team is finalizing exclusive deals and negotiating the best prices just for you. 
                <span className="font-semibold"> Stay tuned for paradise!</span>
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcoming.map(listing => (
                <UpcomingPropertyCard key={listing.id} listing={listing} />
              ))}
            </div>

            <div className="mt-12 text-center">
              <Card className="inline-block p-6 bg-gradient-to-r from-amber-100 to-orange-100 border-2 border-amber-300 shadow-lg">
                <p className="text-amber-800 font-medium flex items-center gap-2">
                  <span className="text-2xl">📞</span>
                  Interested in an upcoming property? 
                  <span className="font-bold">Contact us to get notified first!</span>
                </p>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-white to-[hsl(var(--fiji-sand))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-[var(--font-heading)] text-4xl md:text-5xl tropical-text-gradient mb-4">
              Why Choose Cephas Kumar Real Estate?
            </h2>
            <p className="text-lg text-muted-foreground">Your trusted partner for Fiji's finest properties</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 text-center fiji-luxury-card fiji-premium-shadow hover:scale-105 transition-transform">
              <div className="text-5xl mb-4">🏝️</div>
              <h3 className="font-[var(--font-heading)] text-xl mb-3">Local Expertise</h3>
              <p className="text-muted-foreground">Deep knowledge of Fiji's real estate market across all major islands</p>
            </Card>
            <Card className="p-8 text-center fiji-luxury-card fiji-premium-shadow hover:scale-105 transition-transform">
              <div className="text-5xl mb-4">🤝</div>
              <h3 className="font-[var(--font-heading)] text-xl mb-3">Trusted Service</h3>
              <p className="text-muted-foreground">Transparent dealings and personalized attention to every client</p>
            </Card>
            <Card className="p-8 text-center fiji-luxury-card fiji-premium-shadow hover:scale-105 transition-transform">
              <div className="text-5xl mb-4">💎</div>
              <h3 className="font-[var(--font-heading)] text-xl mb-3">Premium Properties</h3>
              <p className="text-muted-foreground">Handpicked luxury homes, beachfront villas, and investment opportunities</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-16 tropical-ocean-gradient fiji-tapa-pattern">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-[var(--font-heading)] text-3xl md:text-4xl text-white mb-4">
            Ready to Find Your Dream Home in Fiji?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Let us help you discover the perfect property in paradise
          </p>
          <Link to="/properties">
            <Button size="lg" className="bg-white text-[hsl(var(--fiji-ocean))] hover:bg-white/90 text-lg px-8 py-6">
              <span className="mr-2">🌺</span>
              Browse All Properties
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// Upcoming Property Card (for draft listings)
const UpcomingPropertyCard = ({ listing }) => {
  return (
    <Card className="overflow-hidden group relative border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 shadow-lg hover:shadow-xl transition-all duration-300">
      {/* Coming Soon Ribbon */}
      <div className="absolute top-4 right-0 z-10 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-1 text-sm font-bold shadow-md" style={{transform: 'rotate(0deg)'}}>
        🔥 COMING SOON
      </div>
      
      {/* Image with blur overlay */}
      <div className="relative h-56 overflow-hidden">
        <img 
          src={listing.images?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800'} 
          alt={listing.title}
          className="w-full h-full object-cover filter brightness-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-amber-900/60 to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <p className="text-white font-semibold text-lg drop-shadow-lg">{listing.title}</p>
          <p className="text-amber-100 text-sm flex items-center gap-1">
            <span>📍</span> {listing.location}
          </p>
        </div>
      </div>
      
      {/* Property Info */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 text-sm text-amber-700">
            <span className="flex items-center gap-1">🛏️ {listing.bedrooms} beds</span>
            <span className="flex items-center gap-1">🛁 {listing.bathrooms} baths</span>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg p-4 text-center">
          <p className="text-amber-800 text-sm font-medium">
            ⏳ Currently in negotiations...
          </p>
          <p className="text-amber-600 text-xs mt-1">
            We're securing the best deal for you!
          </p>
        </div>
        
        <div className="mt-4 flex gap-2">
          <Button variant="outline" className="flex-1 border-amber-400 text-amber-700 hover:bg-amber-50">
            <span className="mr-1">🔔</span> Notify Me
          </Button>
        </div>
      </div>
    </Card>
  );
};

// Property Card
const PropertyCard = ({ listing }) => {
  const navigate = useNavigate();

  return (
    <article 
      data-testid="property-card"
      className="property-card fiji-luxury-card tropical-shimmer gold-shimmer rounded-xl overflow-hidden cursor-pointer fiji-premium-shadow tropical-hover-lift gold-palm-corner-accent palm-leaf-accent"
      onClick={() => navigate(`/properties/${listing.id}`)}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img 
          src={listing.images?.[0] || 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800'} 
          alt={listing.title}
          className="w-full h-full object-cover"
        />
        {listing.featured && (
          <Badge className="absolute left-4 top-4 fiji-badge text-white border-0 shadow-lg">
            <span className="mr-1">⭐</span> Featured
          </Badge>
        )}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/30 to-transparent"></div>
      </div>
      <div className="p-6 space-y-3 coconut-texture">
        <h3 className="font-[var(--font-heading)] text-xl tracking-tight">{listing.title}</h3>
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <svg className="w-4 h-4 text-[hsl(var(--fiji-lagoon))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-[hsl(var(--fiji-ocean))] font-medium">{listing.location}</span>
        </p>
        <div className="pt-2 border-t border-[hsl(var(--fiji-lagoon))]/20">
          <p className="font-bold text-2xl tabular-nums tropical-text-gradient">
            ${listing.price?.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">FJD</span>
          </p>
        </div>
        <div className="flex gap-4 text-sm text-muted-foreground pt-2">
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            {listing.bedrooms} bed
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
            </svg>
            {listing.bathrooms} bath
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            {listing.area_sqft} sqft
          </span>
        </div>
        <Button data-testid="view-details-button" size="sm" className="w-full mt-3 fiji-button">
          <span className="mr-2">🏝️</span>
          View Island Property
        </Button>
      </div>
    </article>
  );
};

// Properties Listing Page
const PropertiesPage = () => {
  const [listings, setListings] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.location) params.append('location', filters.location);
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.bedrooms) params.append('bedrooms', filters.bedrooms);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      
      const res = await fetch(`${API_URL}/api/listings?${params.toString()}`);
      const data = await res.json();
      setListings(data.listings || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const handleSearch = () => {
    loadListings();
  };

  const handleClearFilters = () => {
    setFilters({});
    loadListings();
  };

  return (
    <div className="py-12 fiji-beach-gradient fiji-tapa-pattern">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-5xl">🏖️</span>
          <h1 className="font-[var(--font-heading)] text-5xl tropical-text-gradient">All Fiji Properties</h1>
        </div>
        <p className="text-lg text-[hsl(var(--fiji-ocean))] mb-10">Explore our curated collection of beachfront villas, island estates, and tropical luxury homes across Fiji</p>
        
        <div data-testid="listing-filters" className="mb-10 p-8 fiji-glass-water rounded-xl fiji-premium-shadow sand-texture">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <Input
              data-testid="filter-location-input"
              placeholder="🏝️ Location in Fiji..."
              className="fiji-tapa-border bg-white/80"
              value={filters.location || ''}
              onChange={(e) => setFilters({...filters, location: e.target.value})}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Select value={filters.bedrooms || ''} onValueChange={(v) => setFilters({...filters, bedrooms: v})}>
              <SelectTrigger data-testid="filter-beds-select" className="fiji-tapa-border bg-white/80">
                <SelectValue placeholder="🛏️ Bedrooms" />
              </SelectTrigger>
              <SelectContent>
                {[1,2,3,4,5,6,7].map(n => (
                  <SelectItem key={n} value={String(n)}>{n}+ Bedrooms</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              placeholder="💰 Min Price (FJD)"
              className="fiji-tapa-border bg-white/80"
              value={filters.min_price || ''}
              onChange={(e) => setFilters({...filters, min_price: e.target.value})}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Input
              type="number"
              placeholder="💵 Max Price (FJD)"
              className="fiji-tapa-border bg-white/80"
              value={filters.max_price || ''}
              onChange={(e) => setFilters({...filters, max_price: e.target.value})}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div className="flex gap-3">
            <Button 
              data-testid="search-button"
              onClick={handleSearch}
              className="fiji-button flex-1"
            >
              <span className="mr-2">🔍</span>
              Search Properties
            </Button>
            <Button 
              data-testid="clear-filters-button"
              onClick={handleClearFilters}
              variant="outline"
              className="fiji-tapa-border"
            >
              <span className="mr-2">✖️</span>
              Clear Filters
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {listings.map(listing => (
            <PropertyCard key={listing.id} listing={listing} />
          ))}
        </div>
        
        {loading && <p className="text-center py-12 text-[hsl(var(--fiji-ocean))]">Loading tropical properties...</p>}
        {!loading && listings.length === 0 && (
          <div className="text-center py-16 fiji-luxury-card rounded-xl p-12 fiji-premium-shadow">
            <p className="text-lg text-muted-foreground mb-4">🏝️ No properties found matching your criteria</p>
            <p className="text-sm text-muted-foreground">Try adjusting your filters to explore more Fiji paradise properties</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Property Detail Page
const PropertyDetailPage = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiry, setInquiry] = useState({ name: '', email: '', phone: '', message: '' });

  useEffect(() => {
    fetch(`${API_URL}/api/listings/${id}`)
      .then(res => res.json())
      .then(setListing)
      .catch(console.error);
  }, [id]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...inquiry, listing_id: id })
      });
      if (res.ok) {
        toast.success('Inquiry sent successfully!');
        setShowInquiry(false);
        setInquiry({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error('Failed to send inquiry');
      }
    } catch (err) {
      toast.error('Error sending inquiry');
    }
  };

  if (!listing) return <div className="py-12 text-center">Loading...</div>;

  return (
    <div className="py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div data-testid="property-gallery" className="mb-8">
          <img 
            src={listing.images?.[0] || 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=1200'} 
            alt={listing.title}
            className="w-full h-[500px] object-cover rounded-lg"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h1 className="font-[var(--font-heading)] text-4xl mb-4">{listing.title}</h1>
            <p className="text-xl text-muted-foreground mb-6">{listing.location}</p>
            
            <div className="grid grid-cols-4 gap-4 mb-8 p-6 bg-muted rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Price</p>
                <p className="text-xl font-semibold">${listing.price?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bedrooms</p>
                <p className="text-xl font-semibold">{listing.bedrooms}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bathrooms</p>
                <p className="text-xl font-semibold">{listing.bathrooms}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Area</p>
                <p className="text-xl font-semibold">{listing.area_sqft} sqft</p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="font-[var(--font-heading)] text-2xl mb-4">Description</h2>
              <p className="text-muted-foreground leading-relaxed">{listing.description}</p>
            </div>

            {listing.amenities?.length > 0 && (
              <div className="mb-8">
                <h2 className="font-[var(--font-heading)] text-2xl mb-4">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {listing.amenities.map((a, i) => (
                    <Badge key={i} variant="secondary">{a}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <Card className="p-6 sticky top-20">
              <h3 className="font-[var(--font-heading)] text-xl mb-4">Interested in this property?</h3>
              <Button 
                data-testid="inquiry-form-button"
                className="w-full" 
                onClick={() => setShowInquiry(true)}
              >
                Send Inquiry
              </Button>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showInquiry} onOpenChange={setShowInquiry}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Inquiry</DialogTitle>
          </DialogHeader>
          <form data-testid="inquiry-form" onSubmit={submitInquiry} className="space-y-4">
            <Input
              placeholder="Your Name"
              value={inquiry.name}
              onChange={(e) => setInquiry({...inquiry, name: e.target.value})}
              required
            />
            <Input
              type="email"
              placeholder="Your Email"
              value={inquiry.email}
              onChange={(e) => setInquiry({...inquiry, email: e.target.value})}
              required
            />
            <Input
              placeholder="Phone (optional)"
              value={inquiry.phone}
              onChange={(e) => setInquiry({...inquiry, phone: e.target.value})}
            />
            <Textarea
              placeholder="Your message..."
              value={inquiry.message}
              onChange={(e) => setInquiry({...inquiry, message: e.target.value})}
              required
              rows={4}
            />
            <Button type="submit" className="w-full">Send Inquiry</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (isRegister) {
        await register(email, password, 'superadmin');
      } else {
        await login(email, password);
      }
      toast.success(isRegister ? 'Registered successfully!' : 'Logged in successfully!');
      navigate('/admin');
    } catch (err) {
      toast.error(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <Card className="w-full max-w-md p-8">
        <h1 className="font-[var(--font-heading)] text-3xl mb-6 text-center">
          {isRegister ? 'Create Account' : 'Sign In'}
        </h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
            data-testid="login-email-input"
          />
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className="pr-12"
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
              tabIndex={-1}
              data-testid="toggle-password-visibility"
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
          <Button 
            type="submit" 
            className="w-full transition-all duration-200" 
            disabled={isLoading}
            data-testid="login-submit-btn"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isRegister ? 'Creating Account...' : 'Signing In...'}
              </span>
            ) : (
              isRegister ? 'Register' : 'Sign In'
            )}
          </Button>
        </form>
        <p className="mt-4 text-center text-sm">
          {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            type="button"
            onClick={() => setIsRegister(!isRegister)}
            className="text-[hsl(var(--accent))] hover:underline"
            disabled={isLoading}
          >
            {isRegister ? 'Sign In' : 'Register'}
          </button>
        </p>
      </Card>
    </div>
  );
};

// Admin Dashboard (Simplified)
const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [activeTab, setActiveTab] = useState('properties');
  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  const loadData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, listingsRes, inquiriesRes] = await Promise.all([
        fetch(`${API_URL}/api/stats`, { headers }),
        fetch(`${API_URL}/api/listings?status=`, { headers }),
        fetch(`${API_URL}/api/inquiries`, { headers })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (listingsRes.ok) {
        const data = await listingsRes.json();
        setListings(data.listings || []);
      }
      if (inquiriesRes.ok) {
        const data = await inquiriesRes.json();
        setInquiries(data.inquiries || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, [token, navigate, loadData]);

  const deleteListing = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      const res = await fetch(`${API_URL}/api/listings/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Listing deleted');
        loadData();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (!user) return null;

  return (
    <div data-testid="admin-shell" className="min-h-screen admin-tropical-bg fiji-tapa-pattern">
      <div className="border-b fiji-glass-water p-4 fiji-premium-shadow">
        <div className="mx-auto max-w-7xl flex justify-between items-center">
          <h1 className="font-[var(--font-heading)] text-3xl tropical-text-gradient flex items-center gap-2">
            <span>🌴</span> Admin Dashboard
          </h1>
          <Link to="/"><Button variant="ghost">Back to Site</Button></Link>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10">
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
            <Card className="p-8 stat-card-fiji fiji-premium-shadow">
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                <span>🏝️</span> Total Listings
              </p>
              <p className="text-4xl font-bold tropical-text-gradient mt-2">{stats.total_listings}</p>
            </Card>
            <Card className="p-8 stat-card-fiji fiji-premium-shadow">
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                <span>✅</span> Active Listings
              </p>
              <p className="text-4xl font-bold tropical-text-gradient mt-2">{stats.active_listings}</p>
            </Card>
            <Card className="p-8 stat-card-fiji fiji-premium-shadow">
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                <span>📧</span> Inquiries
              </p>
              <p className="text-4xl font-bold tropical-text-gradient mt-2">{stats.total_inquiries}</p>
            </Card>
            <Card className="p-8 stat-card-fiji fiji-premium-shadow">
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                <span>💰</span> Avg Price
              </p>
              <p className="text-4xl font-bold tropical-text-gradient mt-2">${Math.round(stats.avg_price).toLocaleString()}</p>
            </Card>
          </div>
        )}

        <div className="mb-10">
          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6 border-b border-[hsl(var(--fiji-lagoon))]/20 pb-4">
            <Button 
              variant={activeTab === 'properties' ? 'default' : 'ghost'} 
              onClick={() => setActiveTab('properties')}
              className={activeTab === 'properties' ? 'fiji-button' : ''}
              data-testid="tab-properties"
            >
              🏖️ Properties
            </Button>
            <Button 
              variant={activeTab === 'crm' ? 'default' : 'ghost'} 
              onClick={() => setActiveTab('crm')}
              className={activeTab === 'crm' ? 'fiji-button' : ''}
              data-testid="tab-crm"
            >
              👥 Client CRM
            </Button>
            <Button 
              variant={activeTab === 'inquiries' ? 'default' : 'ghost'} 
              onClick={() => setActiveTab('inquiries')}
              className={activeTab === 'inquiries' ? 'fiji-button' : ''}
              data-testid="tab-inquiries"
            >
              📬 Inquiries
            </Button>
            <Button 
              variant={activeTab === 'settings' ? 'default' : 'ghost'} 
              onClick={() => setActiveTab('settings')}
              className={activeTab === 'settings' ? 'fiji-button' : ''}
              data-testid="tab-settings"
            >
              ⚙️ Email Settings
            </Button>
            <Button 
              variant={activeTab === 'site' ? 'default' : 'ghost'} 
              onClick={() => setActiveTab('site')}
              className={activeTab === 'site' ? 'fiji-button' : ''}
              data-testid="tab-site"
            >
              🌐 Site Settings
            </Button>
            <Button 
              variant={activeTab === 'account' ? 'default' : 'ghost'} 
              onClick={() => setActiveTab('account')}
              className={activeTab === 'account' ? 'fiji-button' : ''}
              data-testid="tab-account"
            >
              👤 My Account
            </Button>
            <Button 
              variant={activeTab === 'ads' ? 'default' : 'ghost'} 
              onClick={() => setActiveTab('ads')}
              className={activeTab === 'ads' ? 'fiji-button' : ''}
              data-testid="tab-ads"
            >
              📢 Advertising
            </Button>
          </div>

          {/* Properties Tab */}
          {activeTab === 'properties' && (
            <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-[var(--font-heading)] text-3xl tropical-text-gradient flex items-center gap-2">
              <span>🏖️</span> Fiji Properties
            </h2>
            <Button onClick={() => { setEditListing(null); setShowForm(true); }} className="fiji-button">
              <span className="mr-2">➕</span> Add Property
            </Button>
          </div>
          
          <Card className="overflow-hidden fiji-premium-shadow fiji-luxury-card">
            <div data-testid="properties-table" className="overflow-x-auto">
              <table className="w-full">
                <thead className="stat-card-fiji">
                  <tr>
                    <th className="p-4 text-left text-sm font-medium">Title</th>
                    <th className="p-4 text-left text-sm font-medium">Location</th>
                    <th className="p-4 text-left text-sm font-medium">Price (FJD)</th>
                    <th className="p-4 text-left text-sm font-medium">Status</th>
                    <th className="p-4 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map(l => (
                    <tr key={l.id} className="border-t border-[hsl(var(--fiji-lagoon))]/20">
                      <td className="p-4 font-medium">{l.title}</td>
                      <td className="p-4 text-muted-foreground">{l.location}</td>
                      <td className="p-4 font-semibold">${l.price?.toLocaleString()}</td>
                      <td className="p-4">
                        <Badge variant={l.status === 'published' ? 'default' : 'secondary'} className={l.status === 'published' ? 'fiji-badge' : ''}>
                          {l.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => { setEditListing(l); setShowForm(true); }}>
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteListing(l.id)}>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
            </>
          )}

          {/* CRM Tab */}
          {activeTab === 'crm' && <CRMSection token={token} listings={listings} />}

          {/* Inquiries Tab */}
          {activeTab === 'inquiries' && (
        <div>
          <h2 className="font-[var(--font-heading)] text-3xl mb-6 tropical-text-gradient flex items-center gap-2">
            <span>📬</span> Recent Inquiries
          </h2>
          <Card className="fiji-premium-shadow fiji-luxury-card">
            <div className="divide-y divide-[hsl(var(--fiji-lagoon))]/20">
              {inquiries.slice(0, 10).map(inq => (
                <div key={inq.id} className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-lg">{inq.name}</p>
                      <p className="text-sm text-muted-foreground">{inq.email}</p>
                      <p className="text-sm mt-2">{inq.message}</p>
                    </div>
                    <Badge className={inq.status === 'new' ? 'fiji-badge' : ''}>{inq.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
          )}

          {/* Email Settings Tab */}
          {activeTab === 'settings' && <EmailSettingsSection token={token} />}

          {/* Site Settings Tab */}
          {activeTab === 'site' && <SiteSettingsSection token={token} />}

          {/* Account Tab */}
          {activeTab === 'account' && <AccountSection token={token} user={user} />}

          {/* Advertising Tab */}
          {activeTab === 'ads' && <AdsManagementSection token={token} />}
        </div>
      </div>

      {showForm && (
        <PropertyForm 
          listing={editListing} 
          onClose={() => { setShowForm(false); loadData(); }} 
        />
      )}
    </div>
  );
};

// Account Section Component - Password Change
const AccountSection = ({ token, user }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (res.ok) {
        toast.success('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (err) {
      toast.error('Error changing password');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="font-[var(--font-heading)] text-3xl mb-6 tropical-text-gradient flex items-center gap-2">
        <span>👤</span> My Account
      </h2>

      {/* Account Info Card */}
      <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
        <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">📧 Account Information</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <span className="text-muted-foreground w-24">Email:</span>
            <span className="font-medium">{user?.email || 'Loading...'}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-muted-foreground w-24">Role:</span>
            <Badge className="fiji-badge capitalize">{user?.role || 'admin'}</Badge>
          </div>
        </div>
      </Card>

      {/* Password Change Card */}
      <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
        <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">🔒 Change Password</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Update your password to keep your account secure. After changing your password, you'll need to log in again.
        </p>
        
        <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
          <div>
            <label className="text-sm font-medium mb-1 block">Current Password</label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                placeholder="Enter your current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                data-testid="current-password-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? '🙈' : '👁️'}
              </Button>
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">New Password</label>
            <div className="relative">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter your new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? '🙈' : '👁️'}
              </Button>
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">Confirm New Password</label>
            <Input
              type="password"
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              data-testid="confirm-password-input"
            />
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
            )}
          </div>
          
          <Button 
            type="submit" 
            className="fiji-button w-full" 
            disabled={saving || !currentPassword || !newPassword || !confirmPassword}
            data-testid="change-password-btn"
          >
            {saving ? 'Changing Password...' : '🔐 Update Password'}
          </Button>
        </form>
      </Card>
      
      {/* Security Tips */}
      <Card className="p-6 fiji-premium-shadow fiji-luxury-card bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">💡 Security Tips</h3>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li>• Use a strong password with at least 8 characters</li>
          <li>• Include a mix of letters, numbers, and special characters</li>
          <li>• Avoid using personal information in your password</li>
          <li>• Never share your password with anyone</li>
          <li>• Consider changing your password every 3-6 months</li>
        </ul>
      </Card>
    </div>
  );
};

// Ads Management Section Component
const AdsManagementSection = ({ token }) => {
  const [ads, setAds] = useState([]);
  const [adStats, setAdStats] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [showAdForm, setShowAdForm] = useState(false);
  const [editAd, setEditAd] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('overview');

  const loadAdsData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [adsRes, statsRes, pricingRes] = await Promise.all([
        fetch(`${API_URL}/api/ads`, { headers }),
        fetch(`${API_URL}/api/ads/stats/overview`, { headers }),
        fetch(`${API_URL}/api/ad-pricing`)
      ]);

      if (adsRes.ok) {
        const data = await adsRes.json();
        setAds(data.ads || []);
      }
      if (statsRes.ok) {
        setAdStats(await statsRes.json());
      }
      if (pricingRes.ok) {
        const data = await pricingRes.json();
        setPricing(data.pricing || []);
      }
    } catch (err) {
      console.error('Failed to load ads data:', err);
    }
  }, [token]);

  useEffect(() => {
    loadAdsData();
  }, [loadAdsData]);

  const deleteAd = async (id) => {
    if (!window.confirm('Delete this advertisement?')) return;
    try {
      const res = await fetch(`${API_URL}/api/ads/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Ad deleted successfully');
        loadAdsData();
      }
    } catch (err) {
      toast.error('Failed to delete ad');
    }
  };

  const toggleAdStatus = async (ad) => {
    const newStatus = ad.status === 'active' ? 'paused' : 'active';
    try {
      const res = await fetch(`${API_URL}/api/ads/${ad.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success(`Ad ${newStatus === 'active' ? 'activated' : 'paused'}`);
        loadAdsData();
      }
    } catch (err) {
      toast.error('Failed to update ad status');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-[var(--font-heading)] text-3xl mb-6 tropical-text-gradient flex items-center gap-2">
        <span>📢</span> Advertising Management
      </h2>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'overview', label: '📊 Overview' },
          { id: 'ads', label: '🖼️ Manage Ads' },
          { id: 'pricing', label: '💰 Pricing' }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeSubTab === tab.id ? 'default' : 'outline'}
            onClick={() => setActiveSubTab(tab.id)}
            className={activeSubTab === tab.id ? 'fiji-button' : ''}
            size="sm"
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeSubTab === 'overview' && adStats && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
              <p className="text-sm text-muted-foreground mb-1">📢 Total Ads</p>
              <p className="text-3xl font-bold tropical-text-gradient">{adStats.total_ads}</p>
            </Card>
            <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
              <p className="text-sm text-muted-foreground mb-1">✅ Active Ads</p>
              <p className="text-3xl font-bold tropical-text-gradient">{adStats.active_ads}</p>
            </Card>
            <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
              <p className="text-sm text-muted-foreground mb-1">👆 Total Clicks</p>
              <p className="text-3xl font-bold tropical-text-gradient">{adStats.total_clicks}</p>
            </Card>
            <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
              <p className="text-sm text-muted-foreground mb-1">👁️ Impressions</p>
              <p className="text-3xl font-bold tropical-text-gradient">{adStats.total_impressions}</p>
            </Card>
          </div>

          {/* CTR and Revenue */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-6 fiji-premium-shadow fiji-luxury-card bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
              <p className="text-sm text-muted-foreground mb-1">📈 Click-Through Rate</p>
              <p className="text-3xl font-bold text-green-600">{adStats.ctr}%</p>
            </Card>
            <Card className="p-6 fiji-premium-shadow fiji-luxury-card bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20">
              <p className="text-sm text-muted-foreground mb-1">💵 Monthly Revenue</p>
              <p className="text-3xl font-bold text-amber-600">${adStats.total_revenue}</p>
            </Card>
            <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
              <p className="text-sm text-muted-foreground mb-1">📍 By Position</p>
              <div className="flex gap-4 mt-2">
                <div>
                  <span className="text-sm text-muted-foreground">Left:</span>
                  <span className="ml-1 font-bold">{adStats.ads_by_position?.left || 0}</span>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Right:</span>
                  <span className="ml-1 font-bold">{adStats.ads_by_position?.right || 0}</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Position Performance */}
          <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
            <h3 className="font-semibold text-lg mb-4">📊 Performance by Position</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <span className="text-4xl">⬅️</span>
                <p className="font-bold text-xl mt-2">Left Sidebar</p>
                <p className="text-muted-foreground">{adStats.left_sidebar_clicks} clicks</p>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <span className="text-4xl">➡️</span>
                <p className="font-bold text-xl mt-2">Right Sidebar</p>
                <p className="text-muted-foreground">{adStats.right_sidebar_clicks} clicks</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Manage Ads Tab */}
      {activeSubTab === 'ads' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">Manage your advertisements</p>
            <Button 
              onClick={() => { setEditAd(null); setShowAdForm(true); }} 
              className="fiji-button"
            >
              ➕ Add New Ad
            </Button>
          </div>

          <Card className="fiji-premium-shadow fiji-luxury-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium">Ad</th>
                  <th className="text-left p-4 font-medium">Position</th>
                  <th className="text-left p-4 font-medium">Type</th>
                  <th className="text-left p-4 font-medium">Clicks</th>
                  <th className="text-left p-4 font-medium">Impressions</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ads.map(ad => (
                  <tr key={ad.id} className="border-t">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img 
                          src={ad.media_url} 
                          alt={ad.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div>
                          <p className="font-medium">{ad.business_name}</p>
                          <p className="text-xs text-muted-foreground">{ad.title}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={ad.position === 'left' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                        {ad.position === 'left' ? '⬅️ Left' : '➡️ Right'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">
                        {ad.ad_type === 'video' ? '🎬 Video' : ad.ad_type === 'gif' ? '🎞️ GIF' : '🖼️ Image'}
                      </Badge>
                    </td>
                    <td className="p-4 font-bold text-green-600">{ad.clicks}</td>
                    <td className="p-4 text-muted-foreground">{ad.impressions}</td>
                    <td className="p-4">
                      <Badge 
                        className={ad.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-700'}
                      >
                        {ad.status === 'active' ? '✅ Active' : '⏸️ Paused'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => toggleAdStatus(ad)}
                        >
                          {ad.status === 'active' ? '⏸️' : '▶️'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => { setEditAd(ad); setShowAdForm(true); }}
                        >
                          ✏️
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-500"
                          onClick={() => deleteAd(ad.id)}
                        >
                          🗑️
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* Pricing Tab */}
      {activeSubTab === 'pricing' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">Ad pricing tiers for advertisers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pricing.map(tier => (
              <Card key={tier.id} className={`p-6 fiji-premium-shadow fiji-luxury-card ${tier.is_premium ? 'ring-2 ring-amber-400' : ''}`}>
                {tier.is_premium && (
                  <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white mb-2">
                    ⭐ Premium
                  </Badge>
                )}
                <h3 className="font-bold text-lg">{tier.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{tier.description}</p>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Monthly</span>
                    <span className="font-bold text-green-600">${tier.price_monthly}</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Weekly</span>
                    <span>${tier.price_weekly}</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Yearly</span>
                    <span>${tier.price_yearly}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline">{tier.position}</Badge>
                  <Badge variant="outline">{tier.ad_type}</Badge>
                  <Badge variant="outline">{tier.size}</Badge>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Ad Form Dialog */}
      {showAdForm && (
        <AdFormDialog 
          ad={editAd}
          token={token}
          onClose={() => { setShowAdForm(false); setEditAd(null); loadAdsData(); }}
        />
      )}
    </div>
  );
};

// Ad Form Dialog Component
const AdFormDialog = ({ ad, token, onClose }) => {
  const [form, setForm] = useState(ad || {
    title: '',
    business_name: '',
    description: '',
    ad_type: 'image',
    media_url: '',
    video_type: null,
    position: 'left',
    size: 'vertical',
    is_premium: false,
    click_url: '',
    advertiser_name: '',
    advertiser_email: '',
    advertiser_phone: '',
    price_monthly: 100,
    price_type: 'monthly',
    status: 'active'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = ad ? `${API_URL}/api/ads/${ad.id}` : `${API_URL}/api/ads`;
      const method = ad ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(ad ? 'Ad updated successfully!' : 'Ad created successfully!');
        onClose();
      } else {
        toast.error('Failed to save ad');
      }
    } catch (err) {
      toast.error('Error saving ad');
    }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ad ? 'Edit Advertisement' : 'Create New Advertisement'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Business Name *</label>
              <Input
                placeholder="e.g., POS Solutions Fiji"
                value={form.business_name}
                onChange={(e) => setForm({...form, business_name: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Ad Title *</label>
              <Input
                placeholder="e.g., Best IT Solutions"
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                required
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Description</label>
            <Textarea
              placeholder="Short description for the ad overlay..."
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Ad Type *</label>
              <Select value={form.ad_type} onValueChange={(v) => setForm({...form, ad_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">🖼️ Image</SelectItem>
                  <SelectItem value="video">🎬 Video</SelectItem>
                  <SelectItem value="gif">🎞️ Animated GIF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Position *</label>
              <Select value={form.position} onValueChange={(v) => setForm({...form, position: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">⬅️ Left Sidebar</SelectItem>
                  <SelectItem value="right">➡️ Right Sidebar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Size</label>
              <Select value={form.size} onValueChange={(v) => setForm({...form, size: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vertical">📐 Vertical (160x600)</SelectItem>
                  <SelectItem value="square">⬛ Square (300x300)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Media URL *</label>
            <Input
              placeholder="https://example.com/ad-image.jpg"
              value={form.media_url}
              onChange={(e) => setForm({...form, media_url: e.target.value})}
              required
            />
            {form.media_url && (
              <div className="mt-2">
                <img src={form.media_url} alt="Preview" className="w-32 h-auto rounded" />
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Click URL (where ad links to)</label>
            <Input
              placeholder="https://yourbusiness.com"
              value={form.click_url}
              onChange={(e) => setForm({...form, click_url: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Advertiser Name</label>
              <Input
                placeholder="John Doe"
                value={form.advertiser_name}
                onChange={(e) => setForm({...form, advertiser_name: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Advertiser Email</label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={form.advertiser_email}
                onChange={(e) => setForm({...form, advertiser_email: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Advertiser Phone</label>
              <Input
                placeholder="+679 123 4567"
                value={form.advertiser_phone}
                onChange={(e) => setForm({...form, advertiser_phone: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Monthly Price ($)</label>
              <Input
                type="number"
                value={form.price_monthly}
                onChange={(e) => setForm({...form, price_monthly: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">✅ Active</SelectItem>
                  <SelectItem value="paused">⏸️ Paused</SelectItem>
                  <SelectItem value="pending">⏳ Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_premium"
              checked={form.is_premium}
              onChange={(e) => setForm({...form, is_premium: e.target.checked})}
              className="rounded"
            />
            <label htmlFor="is_premium" className="text-sm">⭐ Premium Position (Top of Sidebar)</label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="fiji-button flex-1" disabled={saving}>
              {saving ? 'Saving...' : ad ? '💾 Update Ad' : '➕ Create Ad'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// CRM Section Component
const CRMSection = ({ token, listings }) => {
  const [clients, setClients] = useState([]);
  const [crmStats, setCrmStats] = useState(null);
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBudget, setFilterBudget] = useState('');

  const loadCRMData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [clientsRes, statsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/clients?search=${searchTerm}`, { headers }),
        fetch(`${API_URL}/api/crm-stats`, { headers }),
        fetch(`${API_URL}/api/budget-categories`, { headers })
      ]);

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || []);
      }
      if (statsRes.ok) setCrmStats(await statsRes.json());
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setBudgetCategories(data.categories || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [token, searchTerm]);

  useEffect(() => {
    loadCRMData();
  }, [loadCRMData]);

  const deleteClient = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      const res = await fetch(`${API_URL}/api/clients/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Client deleted');
        loadCRMData();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const notifyMatchingClients = async (listingId) => {
    try {
      const res = await fetch(`${API_URL}/api/notify-clients`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ listing_id: listingId })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Sent ${data.sent} email(s) to matching clients!`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send notifications');
      }
    } catch (err) {
      toast.error('Error sending notifications');
    }
  };

  return (
    <div className="space-y-6">
      {/* CRM Stats */}
      {crmStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-6 stat-card-fiji fiji-premium-shadow">
            <p className="text-sm text-muted-foreground mb-1">👥 Total Clients</p>
            <p className="text-3xl font-bold tropical-text-gradient">{crmStats.total_clients}</p>
          </Card>
          <Card className="p-6 stat-card-fiji fiji-premium-shadow">
            <p className="text-sm text-muted-foreground mb-1">✅ Active Clients</p>
            <p className="text-3xl font-bold tropical-text-gradient">{crmStats.active_clients}</p>
          </Card>
          <Card className="p-6 stat-card-fiji fiji-premium-shadow">
            <p className="text-sm text-muted-foreground mb-1">📊 Budget Categories</p>
            <p className="text-3xl font-bold tropical-text-gradient">{budgetCategories.length}</p>
          </Card>
        </div>
      )}

      {/* Budget Categories */}
      <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">💰 Budget Categories</h3>
          <Button size="sm" onClick={() => setShowCategoryForm(true)} className="fiji-button">
            ➕ Add Category
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {budgetCategories.map(cat => (
            <div key={cat.id} className="bg-gradient-to-r from-[hsl(var(--fiji-lagoon))]/10 to-[hsl(var(--fiji-ocean))]/10 px-4 py-2 rounded-lg flex items-center gap-2">
              <span className="font-medium">{cat.name}</span>
              <span className="text-sm text-muted-foreground">
                ${cat.min_price?.toLocaleString()} - ${cat.max_price?.toLocaleString()}
              </span>
              {crmStats?.categories?.find(c => c.id === cat.id)?.client_count > 0 && (
                <Badge className="fiji-badge">{crmStats.categories.find(c => c.id === cat.id)?.client_count} clients</Badge>
              )}
            </div>
          ))}
          {budgetCategories.length === 0 && (
            <p className="text-muted-foreground">No budget categories yet. Add one to organize clients!</p>
          )}
        </div>
      </Card>

      {/* Client Management */}
      <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">👥 Client Database</h3>
          <div className="flex gap-2">
            <Input
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-48"
            />
            <Button onClick={() => { setEditClient(null); setShowClientForm(true); }} className="fiji-button">
              ➕ Add Client
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="stat-card-fiji">
              <tr>
                <th className="p-3 text-left text-sm font-medium">Name</th>
                <th className="p-3 text-left text-sm font-medium">Contact</th>
                <th className="p-3 text-left text-sm font-medium">Budget Range</th>
                <th className="p-3 text-left text-sm font-medium">Property Types</th>
                <th className="p-3 text-left text-sm font-medium">Status</th>
                <th className="p-3 text-left text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(client => (
                <tr key={client.id} className="border-t border-[hsl(var(--fiji-lagoon))]/20">
                  <td className="p-3">
                    <p className="font-medium">{client.name}</p>
                    {client.source && <p className="text-xs text-muted-foreground">via {client.source}</p>}
                  </td>
                  <td className="p-3">
                    <p className="text-sm">{client.email}</p>
                    <p className="text-xs text-muted-foreground">{client.phone}</p>
                  </td>
                  <td className="p-3 font-semibold">
                    ${client.budget_min?.toLocaleString()} - ${client.budget_max?.toLocaleString()}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {client.property_types?.map((t, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge className={client.status === 'active' ? 'fiji-badge' : ''}>{client.status}</Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditClient(client); setShowClientForm(true); }}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteClient(client.id)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {clients.length === 0 && (
            <p className="text-center text-muted-foreground py-8">No clients yet. Add your first client!</p>
          )}
        </div>
      </Card>

      {/* Quick Notify Section */}
      <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">📧 Notify Clients About Properties</h3>
        <p className="text-sm text-muted-foreground mb-4">Select a property to notify all clients whose budget matches:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {listings.filter(l => l.status === 'published').map(listing => (
            <div key={listing.id} className="flex items-center justify-between p-3 bg-[hsl(var(--fiji-sand))]/20 rounded-lg">
              <div>
                <p className="font-medium text-sm">{listing.title?.substring(0, 30)}...</p>
                <p className="text-xs text-muted-foreground">${listing.price?.toLocaleString()}</p>
              </div>
              <Button size="sm" onClick={() => notifyMatchingClients(listing.id)} className="fiji-button">
                📧 Notify
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Client Form Modal */}
      {showClientForm && (
        <ClientForm 
          client={editClient} 
          token={token}
          budgetCategories={budgetCategories}
          onClose={() => { setShowClientForm(false); loadCRMData(); }} 
        />
      )}

      {/* Budget Category Form Modal */}
      {showCategoryForm && (
        <BudgetCategoryForm 
          token={token}
          onClose={() => { setShowCategoryForm(false); loadCRMData(); }} 
        />
      )}
    </div>
  );
};

// Client Form Component
const ClientForm = ({ client, token, budgetCategories, onClose }) => {
  const [form, setForm] = useState(client || {
    name: '', email: '', phone: '', budget_min: '', budget_max: '',
    property_types: [], preferred_locations: [], notes: '', source: ''
  });
  const [saving, setSaving] = useState(false);

  const propertyTypeOptions = ['House', 'Villa', 'Land', 'Apartment', 'Condo', 'Penthouse', 'Commercial'];
  const sourceOptions = ['Referral', 'Website', 'Social Media', 'Walk-in', 'Phone Inquiry', 'Email', 'Other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = client ? 'PATCH' : 'POST';
      const url = client ? `${API_URL}/api/clients/${client.id}` : `${API_URL}/api/clients`;
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...form,
          budget_min: parseFloat(form.budget_min) || 0,
          budget_max: parseFloat(form.budget_max) || 0
        })
      });

      if (res.ok) {
        toast.success(client ? 'Client updated!' : 'Client added!');
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save client');
      }
    } catch (err) {
      toast.error('Error saving client');
    }
    setSaving(false);
  };

  const togglePropertyType = (type) => {
    const types = form.property_types || [];
    if (types.includes(type)) {
      setForm({...form, property_types: types.filter(t => t !== type)});
    } else {
      setForm({...form, property_types: [...types, type]});
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold text-xl">{client ? 'Edit Client' : 'Add New Client'}</h2>
          <Button variant="ghost" onClick={onClose}>✕</Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            placeholder="Full Name *"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="email"
              placeholder="Email *"
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              required
            />
            <Input
              placeholder="Phone"
              value={form.phone}
              onChange={(e) => setForm({...form, phone: e.target.value})}
            />
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <label className="text-sm font-medium text-blue-800 mb-2 block">💰 Budget Range</label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                placeholder="Min Budget"
                value={form.budget_min}
                onChange={(e) => setForm({...form, budget_min: e.target.value})}
              />
              <Input
                type="number"
                placeholder="Max Budget"
                value={form.budget_max}
                onChange={(e) => setForm({...form, budget_max: e.target.value})}
              />
            </div>
            {/* Quick budget buttons */}
            <div className="flex flex-wrap gap-2 mt-2">
              {budgetCategories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setForm({...form, budget_min: cat.min_price, budget_max: cat.max_price})}
                  className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded"
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">🏠 Property Types Interested In</label>
            <div className="flex flex-wrap gap-2">
              {propertyTypeOptions.map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => togglePropertyType(type)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    form.property_types?.includes(type) 
                      ? 'bg-[hsl(var(--fiji-lagoon))] text-white border-[hsl(var(--fiji-lagoon))]' 
                      : 'border-gray-300 hover:border-[hsl(var(--fiji-lagoon))]'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <Input
            placeholder="Preferred Locations (comma-separated)"
            value={form.preferred_locations?.join(', ')}
            onChange={(e) => setForm({...form, preferred_locations: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
          />

          <Select value={form.source} onValueChange={(v) => setForm({...form, source: v})}>
            <SelectTrigger>
              <SelectValue placeholder="How did they find you?" />
            </SelectTrigger>
            <SelectContent>
              {sourceOptions.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Textarea
            placeholder="Notes about this client..."
            value={form.notes}
            onChange={(e) => setForm({...form, notes: e.target.value})}
            rows={3}
          />

          <div className="flex gap-3 pt-2">
            <Button type="submit" className="flex-1 fiji-button" disabled={saving}>
              {saving ? 'Saving...' : (client ? 'Update Client' : 'Add Client')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// Budget Category Form Component
const BudgetCategoryForm = ({ token, onClose }) => {
  const [form, setForm] = useState({ name: '', min_price: '', max_price: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/budget-categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: form.name,
          min_price: parseFloat(form.min_price) || 0,
          max_price: parseFloat(form.max_price) || 0
        })
      });

      if (res.ok) {
        toast.success('Budget category added!');
        onClose();
      } else {
        toast.error('Failed to add category');
      }
    } catch (err) {
      toast.error('Error adding category');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold text-xl">Add Budget Category</h2>
          <Button variant="ghost" onClick={onClose}>✕</Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            placeholder="Category Name (e.g., Starter Homes)"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              placeholder="Min Price"
              value={form.min_price}
              onChange={(e) => setForm({...form, min_price: e.target.value})}
              required
            />
            <Input
              type="number"
              placeholder="Max Price"
              value={form.max_price}
              onChange={(e) => setForm({...form, max_price: e.target.value})}
              required
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" className="flex-1 fiji-button" disabled={saving}>
              {saving ? 'Saving...' : 'Add Category'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// Email Settings Section Component
const EmailSettingsSection = ({ token }) => {
  const [settings, setSettings] = useState({
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
    from_name: '', from_email: '', use_tls: true
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/email-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.settings) {
            setSettings({...data.settings, smtp_password: ''});
          }
        }
      } catch (err) {
        console.error(err);
      }
      setLoaded(true);
    };
    loadSettings();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/email-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('Email settings saved!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Error saving settings');
    }
    setSaving(false);
  };

  const gmailPreset = () => {
    setSettings({
      ...settings,
      smtp_host: 'smtp.gmail.com',
      smtp_port: 587,
      use_tls: true
    });
  };

  const yahooPreset = () => {
    setSettings({
      ...settings,
      smtp_host: 'smtp.mail.yahoo.com',
      smtp_port: 587,
      use_tls: true
    });
  };

  if (!loaded) return <div className="text-center py-8">Loading...</div>;

  return (
    <Card className="p-6 fiji-premium-shadow fiji-luxury-card max-w-2xl">
      <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">⚙️ Email Configuration</h3>
      <p className="text-sm text-muted-foreground mb-6">
        Configure your email account to send notifications to clients. Your emails will be sent from your own email address.
      </p>

      <div className="flex gap-2 mb-6">
        <Button type="button" variant="outline" onClick={gmailPreset}>
          📧 Gmail Settings
        </Button>
        <Button type="button" variant="outline" onClick={yahooPreset}>
          📧 Yahoo Settings
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">SMTP Host</label>
            <Input
              placeholder="smtp.gmail.com"
              value={settings.smtp_host}
              onChange={(e) => setSettings({...settings, smtp_host: e.target.value})}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">SMTP Port</label>
            <Input
              type="number"
              placeholder="587"
              value={settings.smtp_port}
              onChange={(e) => setSettings({...settings, smtp_port: parseInt(e.target.value)})}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Email Username</label>
            <Input
              placeholder="your.email@gmail.com"
              value={settings.smtp_user}
              onChange={(e) => setSettings({...settings, smtp_user: e.target.value})}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Email Password / App Password</label>
            <Input
              type="password"
              placeholder="Your app password"
              value={settings.smtp_password}
              onChange={(e) => setSettings({...settings, smtp_password: e.target.value})}
              required
            />
          </div>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg text-sm">
          <p className="font-medium text-yellow-800 mb-1">⚠️ For Gmail Users:</p>
          <p className="text-yellow-700">
            You need to use an "App Password" instead of your regular password. 
            Go to Google Account → Security → 2-Step Verification → App passwords.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">From Name</label>
            <Input
              placeholder="Cephas Kumar Real Estate"
              value={settings.from_name}
              onChange={(e) => setSettings({...settings, from_name: e.target.value})}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">From Email</label>
            <Input
              type="email"
              placeholder="your.email@gmail.com"
              value={settings.from_email}
              onChange={(e) => setSettings({...settings, from_email: e.target.value})}
              required
            />
          </div>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.use_tls}
            onChange={(e) => setSettings({...settings, use_tls: e.target.checked})}
          />
          <span className="text-sm">Use TLS (recommended)</span>
        </label>

        <Button type="submit" className="w-full fiji-button" disabled={saving}>
          {saving ? 'Saving...' : '💾 Save Email Settings'}
        </Button>
      </form>
    </Card>
  );
};

// Site Settings Section Component
const SiteSettingsSection = ({ token }) => {
  const [settings, setSettings] = useState({
    company_name: '', company_tagline: '', company_description: '',
    phone: '', email: '', whatsapp: '', address: '', office_hours: '',
    facebook_url: '', instagram_url: '', twitter_url: '', linkedin_url: '', youtube_url: '', tiktok_url: '',
    about_title: '', about_content: '', team_members: [],
    footer_text: '', footer_links: []
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState('company');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/site-settings`);
        if (res.ok) {
          const data = await res.json();
          if (data.settings) {
            setSettings(data.settings);
          }
        }
      } catch (err) {
        console.error(err);
      }
      setLoaded(true);
    };
    loadSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/site-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('Site settings saved!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Error saving settings');
    }
    setSaving(false);
  };

  const addTeamMember = () => {
    setSettings({
      ...settings,
      team_members: [...(settings.team_members || []), { name: '', role: '', image: '', bio: '' }]
    });
  };

  const updateTeamMember = (index, field, value) => {
    const members = [...(settings.team_members || [])];
    members[index] = { ...members[index], [field]: value };
    setSettings({ ...settings, team_members: members });
  };

  const removeTeamMember = (index) => {
    const members = [...(settings.team_members || [])];
    members.splice(index, 1);
    setSettings({ ...settings, team_members: members });
  };

  const addFooterLink = () => {
    setSettings({
      ...settings,
      footer_links: [...(settings.footer_links || []), { label: '', url: '' }]
    });
  };

  const updateFooterLink = (index, field, value) => {
    const links = [...(settings.footer_links || [])];
    links[index] = { ...links[index], [field]: value };
    setSettings({ ...settings, footer_links: links });
  };

  const removeFooterLink = (index) => {
    const links = [...(settings.footer_links || [])];
    links.splice(index, 1);
    setSettings({ ...settings, footer_links: links });
  };

  if (!loaded) return <div className="text-center py-8">Loading settings...</div>;

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: 'company', label: '🏢 Company Info', icon: '🏢' },
          { id: 'contact', label: '📞 Contact', icon: '📞' },
          { id: 'social', label: '📱 Social Media', icon: '📱' },
          { id: 'about', label: '📄 About Page', icon: '📄' },
          { id: 'footer', label: '🦶 Footer', icon: '🦶' }
        ].map(section => (
          <Button
            key={section.id}
            variant={activeSection === section.id ? 'default' : 'outline'}
            onClick={() => setActiveSection(section.id)}
            className={activeSection === section.id ? 'fiji-button' : ''}
            size="sm"
          >
            {section.label}
          </Button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Company Info Section */}
        {activeSection === 'company' && (
          <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
            <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">🏢 Company Information</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Company Name</label>
                <Input
                  placeholder="Cephas Kumar Real Estate"
                  value={settings.company_name}
                  onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Tagline</label>
                <Input
                  placeholder="Your Gateway to Paradise Living in Fiji"
                  value={settings.company_tagline}
                  onChange={(e) => setSettings({...settings, company_tagline: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Company Description</label>
                <Textarea
                  placeholder="Tell visitors about your company..."
                  value={settings.company_description}
                  onChange={(e) => setSettings({...settings, company_description: e.target.value})}
                  rows={4}
                />
              </div>
            </div>
          </Card>
        )}

        {/* Contact Section */}
        {activeSection === 'contact' && (
          <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
            <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">📞 Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">📱 Phone Number</label>
                <Input
                  placeholder="+679 123 4567"
                  value={settings.phone}
                  onChange={(e) => setSettings({...settings, phone: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">📧 Email Address</label>
                <Input
                  type="email"
                  placeholder="info@cephaskumar.com"
                  value={settings.email}
                  onChange={(e) => setSettings({...settings, email: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">💬 WhatsApp Number</label>
                <Input
                  placeholder="+679 123 4567"
                  value={settings.whatsapp}
                  onChange={(e) => setSettings({...settings, whatsapp: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">🕐 Office Hours</label>
                <Input
                  placeholder="Mon-Fri: 9AM-5PM, Sat: 10AM-2PM"
                  value={settings.office_hours}
                  onChange={(e) => setSettings({...settings, office_hours: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium mb-1 block">📍 Office Address</label>
                <Textarea
                  placeholder="123 Beach Road, Suva, Fiji Islands"
                  value={settings.address}
                  onChange={(e) => setSettings({...settings, address: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
          </Card>
        )}

        {/* Social Media Section */}
        {activeSection === 'social' && (
          <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
            <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">📱 Social Media Links</h3>
            <p className="text-sm text-muted-foreground mb-4">Add your social media profile URLs. Leave blank if not applicable.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block flex items-center gap-2">
                  <span className="text-blue-600">📘</span> Facebook
                </label>
                <Input
                  placeholder="https://facebook.com/yourpage"
                  value={settings.facebook_url}
                  onChange={(e) => setSettings({...settings, facebook_url: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block flex items-center gap-2">
                  <span className="text-pink-600">📸</span> Instagram
                </label>
                <Input
                  placeholder="https://instagram.com/yourprofile"
                  value={settings.instagram_url}
                  onChange={(e) => setSettings({...settings, instagram_url: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block flex items-center gap-2">
                  <span className="text-sky-500">🐦</span> Twitter/X
                </label>
                <Input
                  placeholder="https://twitter.com/yourhandle"
                  value={settings.twitter_url}
                  onChange={(e) => setSettings({...settings, twitter_url: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block flex items-center gap-2">
                  <span className="text-blue-700">💼</span> LinkedIn
                </label>
                <Input
                  placeholder="https://linkedin.com/company/yourcompany"
                  value={settings.linkedin_url}
                  onChange={(e) => setSettings({...settings, linkedin_url: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block flex items-center gap-2">
                  <span className="text-red-600">▶️</span> YouTube
                </label>
                <Input
                  placeholder="https://youtube.com/@yourchannel"
                  value={settings.youtube_url}
                  onChange={(e) => setSettings({...settings, youtube_url: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block flex items-center gap-2">
                  <span>🎵</span> TikTok
                </label>
                <Input
                  placeholder="https://tiktok.com/@yourprofile"
                  value={settings.tiktok_url}
                  onChange={(e) => setSettings({...settings, tiktok_url: e.target.value})}
                />
              </div>
            </div>
          </Card>
        )}

        {/* About Page Section */}
        {activeSection === 'about' && (
          <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
            <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">📄 About Page Content</h3>
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium mb-1 block">Page Title</label>
                <Input
                  placeholder="About Cephas Kumar Real Estate"
                  value={settings.about_title}
                  onChange={(e) => setSettings({...settings, about_title: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">About Content</label>
                <Textarea
                  placeholder="Write about your company, history, mission, and values..."
                  value={settings.about_content}
                  onChange={(e) => setSettings({...settings, about_content: e.target.value})}
                  rows={6}
                />
              </div>

              {/* Team Members */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium">👥 Team Members</label>
                  <Button type="button" size="sm" onClick={addTeamMember} variant="outline">
                    ➕ Add Team Member
                  </Button>
                </div>
                <div className="space-y-4">
                  {(settings.team_members || []).map((member, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">Team Member #{idx + 1}</span>
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeTeamMember(idx)}>
                          🗑️ Remove
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          placeholder="Full Name"
                          value={member.name}
                          onChange={(e) => updateTeamMember(idx, 'name', e.target.value)}
                        />
                        <Input
                          placeholder="Role (e.g., Sales Manager)"
                          value={member.role}
                          onChange={(e) => updateTeamMember(idx, 'role', e.target.value)}
                        />
                      </div>
                      <Input
                        placeholder="Profile Image URL"
                        value={member.image}
                        onChange={(e) => updateTeamMember(idx, 'image', e.target.value)}
                      />
                      <Textarea
                        placeholder="Short bio..."
                        value={member.bio}
                        onChange={(e) => updateTeamMember(idx, 'bio', e.target.value)}
                        rows={2}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Footer Section */}
        {activeSection === 'footer' && (
          <Card className="p-6 fiji-premium-shadow fiji-luxury-card">
            <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">🦶 Footer Settings</h3>
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium mb-1 block">Footer Copyright Text</label>
                <Input
                  placeholder="© 2025 Cephas Kumar Real Estate. All rights reserved."
                  value={settings.footer_text}
                  onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                />
              </div>

              {/* Footer Links */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium">🔗 Footer Links</label>
                  <Button type="button" size="sm" onClick={addFooterLink} variant="outline">
                    ➕ Add Link
                  </Button>
                </div>
                <div className="space-y-3">
                  {(settings.footer_links || []).map((link, idx) => (
                    <div key={idx} className="flex gap-3 items-center">
                      <Input
                        placeholder="Link Label (e.g., Privacy Policy)"
                        value={link.label}
                        onChange={(e) => updateFooterLink(idx, 'label', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        placeholder="URL (e.g., /privacy)"
                        value={link.url}
                        onChange={(e) => updateFooterLink(idx, 'url', e.target.value)}
                        className="flex-1"
                      />
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeFooterLink(idx)}>
                        🗑️
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Save Button */}
        <div className="mt-6">
          <Button type="submit" className="w-full fiji-button" disabled={saving}>
            {saving ? 'Saving...' : '💾 Save Site Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Property Form Component
const PropertyForm = ({ listing, onClose }) => {
  const [form, setForm] = useState(listing || {
    title: '', location: '', price: '', bedrooms: 0, master_bedrooms: 0, bathrooms: 1, 
    area_sqft: '', property_type: 'House', description: '', amenities: [],
    features: [], images: [], status: 'draft', featured: false
  });
  const [generating, setGenerating] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [newPhotoLabel, setNewPhotoLabel] = useState('');

  const token = localStorage.getItem('token');

  // Calculate total bedrooms
  const totalBedrooms = (parseInt(form.bedrooms) || 0) + (parseInt(form.master_bedrooms) || 0);

  // Photo labels for quick selection
  const photoLabelOptions = [
    'Cover Photo', 'Living Room', 'Kitchen', 'Dining Room', 
    'Master Bedroom', 'Bedroom 1', 'Bedroom 2', 'Bedroom 3',
    'Bathroom', 'Master Bath', 'Pool', 'Garden', 'Ocean View',
    'Balcony', 'Garage', 'Exterior Front', 'Exterior Back'
  ];

  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingPhotos(true);
    const fileArray = Array.from(files);
    const uploadedPhotos = [];
    
    // First, create a temporary listing if we don't have one yet
    // Or upload to a temp endpoint
    for (const file of fileArray) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await fetch(`${API_URL}/api/upload-image`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          uploadedPhotos.push({
            url: data.url,
            label: newPhotoLabel || `Photo ${(form.images?.length || 0) + uploadedPhotos.length + 1}`
          });
        } else {
          // Fallback: use blob URL temporarily but warn user
          uploadedPhotos.push({
            url: URL.createObjectURL(file),
            label: newPhotoLabel || `Photo ${(form.images?.length || 0) + uploadedPhotos.length + 1}`,
            isTemp: true
          });
        }
      } catch (err) {
        console.error('Upload error:', err);
        // Fallback to blob URL
        uploadedPhotos.push({
          url: URL.createObjectURL(file),
          label: newPhotoLabel || `Photo ${(form.images?.length || 0) + uploadedPhotos.length + 1}`,
          isTemp: true
        });
      }
    }
    
    setForm({...form, images: [...(form.images || []), ...uploadedPhotos]});
    setNewPhotoLabel('');
    setUploadingPhotos(false);
    
    const tempCount = uploadedPhotos.filter(p => p.isTemp).length;
    if (tempCount > 0) {
      toast.warning(`${uploadedPhotos.length} photo(s) added (${tempCount} pending upload on save)`);
    } else {
      toast.success(`${uploadedPhotos.length} photo(s) uploaded!`);
    }
  };

  const updatePhotoLabel = (index, label) => {
    const newImages = [...form.images];
    newImages[index] = { ...newImages[index], label };
    setForm({...form, images: newImages});
  };

  const removePhoto = (index) => {
    const newImages = [...form.images];
    newImages.splice(index, 1);
    setForm({...form, images: newImages});
    if (currentPhotoIndex >= newImages.length && newImages.length > 0) {
      setCurrentPhotoIndex(newImages.length - 1);
    }
  };

  const movePhoto = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= form.images.length) return;
    const newImages = [...form.images];
    const [movedItem] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedItem);
    setForm({...form, images: newImages});
    setCurrentPhotoIndex(toIndex);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = listing ? 'PATCH' : 'POST';
      const url = listing ? `${API_URL}/api/listings/${listing.id}` : `${API_URL}/api/listings`;
      
      // Prepare images for submission (extract URLs)
      const imageUrls = form.images?.map(img => typeof img === 'string' ? img : img.url) || [];
      
      const submitData = {
        ...form, 
        images: imageUrls,
        bedrooms: totalBedrooms,  // Send total bedrooms to backend
        bedroom_breakdown: {
          regular: parseInt(form.bedrooms) || 0,
          master: parseInt(form.master_bedrooms) || 0
        }
      };
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (res.ok) {
        toast.success(listing ? 'Listing updated!' : 'Listing created!');
        onClose();
      } else {
        toast.error('Failed to save listing');
      }
    } catch (err) {
      toast.error('Error saving listing');
    }
  };

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          property_type: form.property_type,
          price: parseFloat(form.price),
          location: form.location,
          bedrooms: totalBedrooms,
          master_bedrooms: parseInt(form.master_bedrooms) || 0,
          bathrooms: parseFloat(form.bathrooms),
          area_sqft: parseInt(form.area_sqft),
          amenities: form.amenities,
          features: form.features
        })
      });

      if (res.ok) {
        const data = await res.json();
        setForm({...form, title: data.title, description: data.description});
        toast.success('Title & description generated!');
      } else {
        toast.error('Failed to generate content');
      }
    } catch (err) {
      toast.error('Error generating content');
    }
    setGenerating(false);
  };

  // Get current photo for carousel
  const currentPhoto = form.images?.[currentPhotoIndex];
  const hasPhotos = form.images && form.images.length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-3xl p-6 my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-[var(--font-heading)] text-2xl">
            {listing ? 'Edit Property' : 'Add Property'}
          </h2>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>

        <form data-testid="property-crud-form" onSubmit={handleSubmit} className="space-y-5">
          
          {/* PHOTO UPLOAD SECTION */}
          <div className="bg-gradient-to-br from-[hsl(var(--fiji-sand)/0.3)] to-[hsl(var(--fiji-lagoon)/0.1)] rounded-xl p-5 border-2 border-dashed border-[hsl(var(--fiji-lagoon)/0.3)]">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              📷 Property Photos
              {hasPhotos && <span className="text-sm font-normal text-muted-foreground">({form.images.length} photos)</span>}
            </h3>
            
            {/* Photo Carousel */}
            {hasPhotos && (
              <div className="mb-4">
                <div className="relative bg-black rounded-xl overflow-hidden" style={{height: '280px'}}>
                  {/* Main Photo */}
                  <img 
                    src={typeof currentPhoto === 'string' ? currentPhoto : currentPhoto?.url} 
                    alt={typeof currentPhoto === 'string' ? 'Photo' : currentPhoto?.label}
                    className="w-full h-full object-contain"
                  />
                  
                  {/* Photo Label Badge */}
                  <div className="absolute top-3 left-3 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {typeof currentPhoto === 'string' ? `Photo ${currentPhotoIndex + 1}` : currentPhoto?.label}
                  </div>
                  
                  {/* Photo Counter */}
                  <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentPhotoIndex + 1} / {form.images.length}
                  </div>
                  
                  {/* Navigation Arrows */}
                  {form.images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setCurrentPhotoIndex(prev => prev > 0 ? prev - 1 : form.images.length - 1)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-all hover:scale-110"
                      >
                        ◀
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPhotoIndex(prev => prev < form.images.length - 1 ? prev + 1 : 0)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-all hover:scale-110"
                      >
                        ▶
                      </button>
                    </>
                  )}
                  
                  {/* Delete & Reorder Controls */}
                  <div className="absolute bottom-3 right-3 flex gap-2">
                    {currentPhotoIndex > 0 && (
                      <button
                        type="button"
                        onClick={() => movePhoto(currentPhotoIndex, currentPhotoIndex - 1)}
                        className="bg-blue-500 hover:bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm"
                        title="Move left"
                      >
                        ←
                      </button>
                    )}
                    {currentPhotoIndex < form.images.length - 1 && (
                      <button
                        type="button"
                        onClick={() => movePhoto(currentPhotoIndex, currentPhotoIndex + 1)}
                        className="bg-blue-500 hover:bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm"
                        title="Move right"
                      >
                        →
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(currentPhotoIndex)}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center"
                      title="Delete photo"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                {/* Edit Label for Current Photo */}
                <div className="mt-2 flex gap-2">
                  <Select 
                    value={typeof currentPhoto === 'string' ? '' : currentPhoto?.label || ''} 
                    onValueChange={(v) => updatePhotoLabel(currentPhotoIndex, v)}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="📝 Label this photo..." />
                    </SelectTrigger>
                    <SelectContent className="z-[60]">
                      {photoLabelOptions.map(label => (
                        <SelectItem key={label} value={label}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Thumbnail Strip */}
                <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                  {form.images.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCurrentPhotoIndex(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        idx === currentPhotoIndex ? 'border-[hsl(var(--fiji-lagoon))] ring-2 ring-[hsl(var(--fiji-lagoon))]' : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img 
                        src={typeof img === 'string' ? img : img.url} 
                        alt={typeof img === 'string' ? `Photo ${idx + 1}` : img.label}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Add Photos Section */}
            <div className="space-y-3">
              <div className="flex gap-2 items-center">
                <Select value={newPhotoLabel} onValueChange={setNewPhotoLabel}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="📝 Photo label..." />
                  </SelectTrigger>
                  <SelectContent className="z-[60]">
                    {photoLabelOptions.map(label => (
                      <SelectItem key={label} value={label}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    id="photo-upload-input"
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[hsl(var(--fiji-lagoon))] file:text-white hover:file:bg-[hsl(var(--fiji-ocean))] file:cursor-pointer cursor-pointer"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {newPhotoLabel ? `Selected label: "${newPhotoLabel}" - photos will be labeled automatically` : 'Select a label first, then choose photos to upload'}
              </p>
            </div>
          </div>

          {/* PROPERTY DETAILS */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              🏡 Property Details
            </h3>
            
            <Input
              placeholder="Location (e.g., Denarau Island, Fiji)"
              value={form.location}
              onChange={(e) => setForm({...form, location: e.target.value})}
              required
            />
            
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number"
                placeholder="Price (FJD)"
                value={form.price}
                onChange={(e) => setForm({...form, price: e.target.value})}
                required
              />
              <Input
                type="number"
                placeholder="Area (sqft)"
                value={form.area_sqft}
                onChange={(e) => setForm({...form, area_sqft: e.target.value})}
                required
              />
            </div>
            
            {/* BEDROOM BREAKDOWN */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <label className="text-sm font-semibold text-blue-800 mb-3 block">
                🛏️ Bedroom Configuration
              </label>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="text-xs text-blue-600 mb-1 block">Regular Bedrooms</label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={form.bedrooms}
                    onChange={(e) => setForm({...form, bedrooms: e.target.value})}
                    className="bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-blue-600 mb-1 block">Master Bedrooms</label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={form.master_bedrooms}
                    onChange={(e) => setForm({...form, master_bedrooms: e.target.value})}
                    className="bg-white"
                  />
                </div>
              </div>
              <div className="bg-blue-100 rounded-lg p-3 text-center">
                <span className="text-blue-800 font-semibold">
                  Total: {totalBedrooms} Bedroom{totalBedrooms !== 1 ? 's' : ''}
                  {(parseInt(form.master_bedrooms) || 0) > 0 && (
                    <span className="text-blue-600 font-normal">
                      {' '}({form.bedrooms || 0} regular + {form.master_bedrooms} master)
                    </span>
                  )}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Bathrooms</label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  placeholder="Bathrooms"
                  value={form.bathrooms}
                  onChange={(e) => setForm({...form, bathrooms: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Property Type</label>
                <Select value={form.property_type} onValueChange={(v) => setForm({...form, property_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[60]">
                    <SelectItem value="House">House</SelectItem>
                    <SelectItem value="Condo">Condo</SelectItem>
                    <SelectItem value="Apartment">Apartment</SelectItem>
                    <SelectItem value="Penthouse">Penthouse</SelectItem>
                    <SelectItem value="Villa">Villa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Input
              placeholder="Amenities (comma-separated: Pool, Ocean View, Gym...)"
              value={form.amenities?.join(', ')}
              onChange={(e) => setForm({...form, amenities: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
            />
          </div>

          {/* AI GENERATION SECTION */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-5 border border-purple-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                ✨ AI-Generated Content
              </h3>
              <Button
                type="button"
                data-testid="ai-generate-button"
                variant="default"
                size="sm"
                onClick={generateWithAI}
                disabled={generating || !form.price || !form.location || totalBedrooms < 1}
                className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
              >
                {generating ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </span>
                ) : (
                  '🪄 Generate Title & Description'
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Fill in location, price & bedrooms above, then click to auto-generate a compelling title and description!
            </p>
            
            <Input
              placeholder="Property Title (auto-generated or custom)"
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              required
              className="mb-3"
            />
            
            <Textarea
              placeholder="Property description (auto-generated or custom)..."
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              rows={5}
            />
          </div>

          {/* PUBLISH OPTIONS */}
          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({...form, featured: e.target.checked})}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium">⭐ Featured Property</span>
            </label>
            <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[60]">
                <SelectItem value="draft">📝 Draft</SelectItem>
                <SelectItem value="published">✅ Published</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1 bg-[hsl(var(--fiji-lagoon))] hover:bg-[hsl(var(--fiji-ocean))]">
              {listing ? '💾 Update Property' : '✨ Create Property'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="py-12 text-center">Loading...</div>;
  return user ? children : <Navigate to="/login" />;
};

// Main App
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-background">
          <Header />
          <AdSidebars />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/properties" element={<PropertiesPage />} />
            <Route path="/properties/:id" element={<PropertyDetailPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          </Routes>
          <Toaster />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
